import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { LangProvider } from "@/lib/lang";
import SmoothScroll from "@/components/SmoothScroll";
import SiteHead from "@/components/SiteHead";
import ConsentBanner from "@/components/ConsentBanner";
import Home from "@/pages/Home";
import { captureAttribution } from "@/lib/attribution";

// Code-split every non-home route. The home page ships in the main bundle (LCP-critical);
// every other route is fetched on demand so the first paint stays lean.
const Products = lazy(() => import("@/pages/Products"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const Contact = lazy(() => import("@/pages/Contact"));
const Consultation = lazy(() => import("@/pages/Consultation"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const ShowroomPage = lazy(() => import("@/pages/ShowroomPage"));
const BlogList = lazy(() => import("@/pages/BlogList"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const TradePartnerPage = lazy(() => import("@/pages/TradePartnerPage"));
const TradePartnerApply = lazy(() => import("@/pages/TradePartnerApply"));
const InstallationRemodeling = lazy(() => import("@/pages/InstallationRemodeling"));
const InstallationRequest = lazy(() => import("@/pages/InstallationRequest"));
const BuilderPage = lazy(() => import("@/pages/B2BPage").then(m => ({ default: m.BuilderPage })));
const DeveloperPage = lazy(() => import("@/pages/B2BPage").then(m => ({ default: m.DeveloperPage })));
const RFQPage = lazy(() => import("@/pages/RFQPage"));
const QuotePortal = lazy(() => import("@/pages/QuotePortal"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminForgotPassword = lazy(() => import("@/pages/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("@/pages/AdminResetPassword"));
const EmployeeLogin = lazy(() => import("@/pages/EmployeeLogin"));
const EmployeePortal = lazy(() => import("@/pages/EmployeePortal"));
const TradePartnerLogin = lazy(() => import("@/pages/TradePartnerLogin"));
const TradePartnerDashboard = lazy(() => import("@/pages/TradePartnerDashboard"));
const TradePartnerActivate = lazy(() => import("@/pages/TradePartnerActivate"));
const TradePartnerResetPassword = lazy(() => import("@/pages/TradePartnerResetPassword"));
const TradePartnerForgotPassword = lazy(() => import("@/pages/TradePartnerForgotPassword"));

function RouteFallback() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#FDFBF7",
      }}
      aria-busy="true"
    >
      <div
        style={{
          width: 36,
          height: 36,
          border: "2px solid #D7CFC4",
          borderTopColor: "#FF6A00",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

/**
 * Runs inside <BrowserRouter> so it can useLocation(). Captures UTMs on first
 * mount and pushes a `virtual_page_view` to GTM `dataLayer` on every SPA route
 * change so GTM Preview + Google Analytics see every navigation.
 */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    captureAttribution();
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Consent gate — do not push tracking events unless the visitor accepted.
    try {
      const c = JSON.parse(window.localStorage.getItem("toa_consent_v1") || "null");
      if (!c || c.marketing !== true) return;
    } catch { return; }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "virtual_page_view",
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
      if (typeof window.fbq === "function") {
        window.fbq("track", "PageView");
      }
    } catch { /* ignore */ }
  }, [location.pathname, location.search]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <RouteTracker />
            <SmoothScroll />
            <SiteHead />
            <Toaster position="bottom-right" theme="light" richColors closeButton />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/catalog" element={<CatalogPage />} />
                <Route path="/showroom" element={<ShowroomPage />} />
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/trade-partner" element={<TradePartnerPage />} />
                <Route path="/trade-partners/apply" element={<TradePartnerApply />} />
                <Route path="/installation-remodeling" element={<InstallationRemodeling />} />
                <Route path="/installation/request" element={<InstallationRequest />} />
                <Route path="/builder" element={<BuilderPage />} />
                <Route path="/developer" element={<DeveloperPage />} />
                <Route path="/rfq" element={<RFQPage />} />
                <Route path="/quote/:shareToken" element={<QuotePortal />} />
                <Route path="/pay/:token" element={<CheckoutPage />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentSuccess />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                <Route path="/employee" element={<EmployeePortal />} />
                <Route path="/employee/login" element={<EmployeeLogin />} />
                <Route path="/employee/reset-password" element={<AdminResetPassword />} />
                <Route path="/trade-partner/login" element={<TradePartnerLogin />} />
                <Route path="/trade-partner/dashboard" element={<TradePartnerDashboard />} />
                <Route path="/trade-partner/activate" element={<TradePartnerActivate />} />
                <Route path="/trade-partner/reset-password" element={<TradePartnerResetPassword />} />
                <Route path="/trade-partner/forgot-password" element={<TradePartnerForgotPassword />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            <ConsentBanner />
          </BrowserRouter>
        </AuthProvider>
      </LangProvider>
    </HelmetProvider>
  );
}
